// src/components/resources/quizzes/components/question-types/NumberQuestion.tsx

'use client'

import { useState, type ReactNode } from 'react'
import { cn } from '~/lib/utils'
import { formatNumber } from '~/lib/utils/formatters'
import RiCheckLine from '~/icons/remix/generated/RiCheckLine'
import RiCloseLine from '~/icons/remix/generated/RiCloseLine'
import { Button } from '~/components/ui/Button'
import type { QuizQuestion } from '~/lib/resources/types/tools/quiz'

interface NumberQuestionProps {
  question: QuizQuestion
  selectedAnswer: string | string[]
  onAnswerSelect: (answer: string) => void
  showFeedback: boolean
  isAnswerCorrect?: boolean
  disabled?: boolean
}

/**
 * Helper function to format a number with a unit
 * 
 * @param {number} value - The numeric value to format
 * @param {string} [unit] - Optional unit to append to the value
 * @param {number} [decimals=2] - Number of decimal places to use
 * @returns {string} Formatted number with unit
 */
function formatNumberWithUnit(value: number, unit?: string, decimals = 2): string {
  const formattedValue = formatNumber(value, decimals)
  return unit ? `${formattedValue} ${unit}` : formattedValue
}

/**
 * Normalizes a string input to a number, handling various formats
 * 
 * @param {unknown} value - The value to normalize
 * @returns {number} Normalized number or NaN
 */
function parseNumberInput(value: unknown): number {
  if (typeof value === 'number') return value
  if (typeof value !== 'string') return NaN
  
  // Convert to string for processing
  const strValue = value.toString().trim()
  if (!strValue) return NaN
  
  // Remove commas and spaces, replace multiple dots with single dot
  const normalized = strValue
    .replace(/,|\s/g, '')
    .replace(/\.+/g, '.')
    .replace(/[^\d.-]/g, '') // Remove any non-numeric chars except dots and minus

  // Handle edge cases
  if ((normalized.match(/-/g) ?? []).length > 1) return NaN // Multiple minus signs
  if ((normalized.match(/\./g) ?? []).length > 1) return NaN // Multiple decimal points
  if (normalized === '-' || normalized === '.') return NaN
  
  return parseFloat(normalized)
}

/**
 * Checks if a number is within a specified range
 * 
 * @param {number} num - The number to check
 * @param {{ min: number, max: number }} [range] - The range to check against
 * @returns {boolean} True if the number is within range or if no range is specified
 */
function isNumberInRange(num: number, range?: { min: number; max: number }): boolean {
  if (isNaN(num) || !range) return true
  return num >= range.min && num <= range.max
}

export function NumberQuestion({
  question,
  selectedAnswer,
  onAnswerSelect,
  showFeedback,
  isAnswerCorrect,
  disabled
}: NumberQuestionProps): ReactNode {
  const [isFocused, setIsFocused] = useState(false)
  const [draftAnswer, setDraftAnswer] = useState(selectedAnswer as string ?? '')
  const [error, setError] = useState<string | null>(null)
  const answer = selectedAnswer as string ?? ''
  
  // Use the isAnswerCorrect prop from parent
  const isCorrect = showFeedback ? isAnswerCorrect : false

  const handleSubmit = (): void => {
    if (typeof draftAnswer !== 'string' || !draftAnswer.trim()) return
    
    const normalized = parseNumberInput(draftAnswer)
    if (isNaN(normalized)) {
      setError('Please enter a valid number')
      return
    }

    if (!isNumberInRange(normalized, question.range)) {
      const min = formatNumberWithUnit(question.range?.min ?? 0, question.unit)
      const max = formatNumberWithUnit(question.range?.max ?? 0, question.unit)
      setError(`Value must be between ${min} and ${max}`)
      return
    }

    onAnswerSelect(normalized.toString())
    setError(null)
  }

  const handleKeyDown = (e: React.KeyboardEvent): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSubmit()
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const value = e.target.value
    // Allow empty string, minus sign, decimal point, commas, and spaces during input
    if (value === '' || value === '-' || /^-?[\d,.\s]*$/.test(value)) {
      setDraftAnswer(value)
      setError(null)
    }
  }

  return (
    <div className="space-y-4">
      <div className="relative">
        <input
          type="text"
          inputMode="decimal"
          value={draftAnswer}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          disabled={disabled}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          placeholder={question.placeholder ?? 'Enter a number...'}
          className={cn(
            "w-full rounded-lg border bg-white px-4 py-3 text-base transition-all dark:bg-gray-800",
            disabled
              ? "cursor-not-allowed opacity-50"
              : "focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500 dark:focus:border-indigo-400 dark:focus:ring-indigo-400",
            isFocused
              ? "border-indigo-500 ring-2 ring-indigo-500 dark:border-indigo-400 dark:ring-indigo-400"
              : "border-gray-300 dark:border-gray-700",
            showFeedback && answer && (isCorrect
              ? "border-green-500 ring-2 ring-green-500 dark:border-green-400 dark:ring-green-400"
              : "border-red-500 ring-2 ring-red-500 dark:border-red-400 dark:ring-red-400"),
            error && "border-red-500 ring-2 ring-red-500 dark:border-red-400 dark:ring-red-400"
          )}
        />

        {/* Unit Display */}
        {question.unit && !showFeedback && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400">
            {question.unit}
          </div>
        )}

        {/* Feedback Icon */}
        {showFeedback && answer && !question.explanation && (
          <div className="absolute right-3 top-1/2 -translate-y-1/2">
            {isCorrect ? (
              <RiCheckLine className="h-5 w-5 text-green-500 dark:text-green-400" />
            ) : (
              <RiCloseLine className="h-5 w-5 text-red-500 dark:text-red-400" />
            )}
          </div>
        )}
      </div>

      {/* Error Message */}
      {error && (
        <p className="text-sm text-red-600 dark:text-red-400">
          {error}
        </p>
      )}

      {/* Submit Button */}
      {!showFeedback && draftAnswer && draftAnswer !== answer && !error && (
        <div className="flex justify-end">
          <Button
            onClick={handleSubmit}
            className="w-[120px]"
          >
            Submit
          </Button>
        </div>
      )}

      {/* Range Hint */}
      {question.range && !showFeedback && (
        <p className="text-xs text-gray-500 dark:text-gray-400">
          Expected range: {formatNumberWithUnit(question.range.min, question.unit)} to {formatNumberWithUnit(question.range.max, question.unit)}
        </p>
      )}

      {/* Feedback Message - Only show if there's no explanation */}
      {showFeedback && answer && !question.explanation && (
        <div
          className={cn(
            "rounded-lg p-4",
            isCorrect
              ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300"
              : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300"
          )}
        >
          <p className="text-sm">
            {isCorrect ? (
              "Correct!"
            ) : (
              <>
                Incorrect. The answer should be {formatNumberWithUnit(parseNumberInput(question.correctAnswer), question.unit)}
                {question.tolerance !== undefined && (
                  <span className="mt-1 block text-xs opacity-75">
                    (Acceptable range: {formatNumberWithUnit(parseNumberInput(question.correctAnswer) - question.tolerance, question.unit)} to {formatNumberWithUnit(parseNumberInput(question.correctAnswer) + question.tolerance, question.unit)})
                  </span>
                )}
              </>
            )}
          </p>
        </div>
      )}
    </div>
  )
}